import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  input,
  LOCALE_ID,
  output,
  signal,
} from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { DrawerModule } from 'primeng/drawer';
import { MenuModule } from 'primeng/menu';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { TooltipModule } from 'primeng/tooltip';
import { ImpersonationService } from '../../services/impersonation.service';
import { UserService } from '../../services/user';
import { WithdrawalService } from '../../services/withdrawal.service';
import { UserMenu } from '../user-menu/user-menu';

type RegulatoryMenuItem = MenuItem & {
  action?: 'withdrawal';
};

@Component({
  selector: 'app-sidebar',
  imports: [
    CommonModule,
    RouterLink,
    RouterLinkActive,
    UserMenu,
    ButtonModule,
    TooltipModule,
    MenuModule,
    BadgeModule,
    OverlayBadgeModule,
    DrawerModule,
    AvatarModule,
    DividerModule,
  ],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.css',
  standalone: true,
})
export class Sidebar {
  private userService = inject(UserService);
  private withdrawalService = inject(WithdrawalService);
  protected readonly impersonationService = inject(ImpersonationService);
  protected readonly router = inject(Router);
  private localeId = inject(LOCALE_ID);

  collapsed = input<boolean>(false);
  toggle = output<void>();

  // Notification count and flag for unread notifications
  notificationCount = 3;
  hasUnreadNotifications = true;

  private isCroatianLocale = computed(
    () =>
      typeof this.localeId === 'string' &&
      this.localeId.toLowerCase().startsWith('hr'),
  );

  toggleSidebar() {
    this.toggle.emit();
  }

  navItems = computed(() => {
    if (this.userService.isPracticeEmployee()) {
      return [
        {
          label: $localize`Dashboard`,
          icon: 'far fa-chart-line',
          routerLink: '/',
          exact: true,
          disabled: false,
        },
        {
          label: $localize`Practice Details`,
          icon: 'far fa-house',
          routerLink: '/practice-details',
          exact: false,
          disabled: false,
          tourKey: 'nav-practice-details',
        },
        {
          label: $localize`Patients`,
          icon: 'far fa-person-simple',
          routerLink: '/patients',
          exact: false,
          disabled: false,
          tourKey: 'nav-patients',
        },
        ...(this.userService.isPracticeManagement() ||
        this.userService.isPracticeOwner()
          ? [
              {
                label: $localize`Employees`,
                icon: 'far fa-users',
                routerLink: '/employees',
                exact: false,
                disabled: false,
                tourKey: 'nav-employees',
              },
            ]
          : []),
        // add div that separates the two sections
        {
          label: '',
          icon: '',
          routerLink: '',
          exact: false,
          disabled: false,
          isSpacer: true,
        },
        ...(this.userService.currentUser()?.canPurchaseLicenses
          ? [
              {
                label: $localize`Licenses`,
                icon: 'far fa-key',
                routerLink: '/licenses',
                exact: false,
                disabled: false,
              },
            ]
          : []),
        ...(this.userService.isPracticeManagement() ||
        this.userService.isPracticeOwner()
          ? [
              {
                label: $localize`Billing`,
                icon: 'far fa-credit-card',
                routerLink: '/billing',
                exact: false,
                disabled: false,
              },
            ]
          : []),
      ];
    } else {
      return [
        {
          label: $localize`Dashboard`,
          icon: 'far fa-home',
          routerLink: '/',
          exact: true,
          disabled: false,
        },
        {
          label: $localize`Statistics`,
          icon: 'far fa-chart-line',
          routerLink: '/statistics',
          exact: true,
          disabled: false,
        },
        {
          label: $localize`Achievements`,
          icon: 'far fa-trophy',
          routerLink: '/achievements',
          exact: false,
          disabled: false,
        },
        ...(this.userService.isAdmin()
          ? [
              {
                label: $localize`:@@sidebar.results:Results`,
                icon: 'far fa-list-check',
                routerLink: '/sentence-attempts',
                exact: false,
                disabled: false,
              },
              {
                label: $localize`License Dashboard`,
                icon: 'far fa-chart-pie',
                routerLink: '/license-dashboard',
                exact: false,
                disabled: false,
              },
              {
                label: $localize`:@@sidebar.withdrawals:Withdrawals`,
                icon: 'far fa-file-contract',
                routerLink: '/withdrawals',
                exact: false,
                disabled: false,
              },
              {
                label: $localize`Settings`,
                icon: 'far fa-cog',
                routerLink: '/settings',
                exact: false,
                disabled: false,
              },
            ]
          : []),
      ];
    }
  });

  navItemsRegulatory = signal<RegulatoryMenuItem[]>([
    {
      label: $localize`Privacy Policy`,
      routerLink: '/privacy-policy',
    },
    {
      label: $localize`Terms and Conditions`,
      routerLink: '/agb',
    },
    {
      label: $localize`Imprint`,
      url: 'https://www.lalino.eu/?page_id=112',
    },
    {
      label: $localize`:@@withdrawal.open:Cancel contract`,
      action: 'withdrawal',
    },
  ]);

  visible = false;

  openRegulatoryItem(item: RegulatoryMenuItem, event?: Event): void {
    if (item.action === 'withdrawal') {
      event?.preventDefault();
      this.withdrawalService.openDialog();
      this.visible = false;
    }
  }

  toggleDrawer() {
    this.visible = !this.visible;
  }

  closeCallback(event: Event) {
    event.preventDefault();
    this.visible = false;
  }
}
